import AddCircle from "@mui/icons-material/AddCircle";
import CommentIcon from "@mui/icons-material/Comment";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { Link } from "react-router-dom";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import { Checkbox, Chip, IconButton, MenuItem, Select, TextField, Tooltip, Typography } from "@mui/material";
import React from "react";
import { EMPLOYEE_TEMP_LABEL, SELECT_FREEDAY_BID_TYPE_LIST, SENIORITY_MISSING_DATA, FEATURES } from "../constants";
import { sortSeniorityList } from "../filterSeniorityList";
import { BID_GROUP_ADD_EMPLOYEE_WARNING_MESSAGE } from "../messageConstants";
import { isDeletedBid, isAwardedBid, isPendingBid } from "./bidCommon.js";
import { getEmployeeUnrankedCount, getEligibleEmployees } from "./bidGroupUtils";
import { ALLOTMENT_TYPES, FREEDAY_BID_TYPE } from "./bidConstants.js";
import { DateTime } from "luxon";

export const getStandardEmployeesCount = (bidGroup) => {
  return bidGroup?.filter((employee) => employee.standardRelief === FREEDAY_BID_TYPE.STANDARD && !employee.isRemoved)
    .length;
};

export const getReliefEmployeesCount = (bidGroup) => {
  return bidGroup?.filter((employee) => employee.standardRelief === FREEDAY_BID_TYPE.RELIEF && !employee.isRemoved)
    .length;
};

export const getTotalStandardAllotments = (dayState) => {
  let totalAllotmentCount = 0;
  for (const day of dayState) {
    totalAllotmentCount += day.amCount + day.pmCount + day.eitherCount + day.amOrPmCount;
  }
  return totalAllotmentCount;
};

export const getTotalReliefAllotments = (dayState) => {
  let totalAllotmentCount = 0;
  for (const day of dayState) {
    totalAllotmentCount += day.amCount + day.pmCount + day.amOrPmCount;
  }
  return totalAllotmentCount;
};

function isEmployeeEligibleForFreedayBidChoices(employeeRow, initialBidGroup, bidStatus, userSessionEid) {
  if (
    employeeRow.isRemoved ||
    employeeRow.eid === userSessionEid ||
    isPendingBid(bidStatus) ||
    isDeletedBid(bidStatus) ||
    isAwardedBid(bidStatus)
  )
    return false;
  else {
    let idx = initialBidGroup.findIndex((emp) => emp.eid === employeeRow.eid);
    if (idx === -1) return false;
  }
  return true;
}

const isEmployeeEligibleForBaseSchedule = (employeeRow, initialBidGroup, bidStatus, bidStation) => {
  if (
    employeeRow.isRemoved ||
    isDeletedBid(bidStatus) ||
    isAwardedBid(bidStatus) ||
    (employeeRow.isTemp && employeeRow.homeStation !== bidStation)
  )
    return false;
  else {
    let idx = initialBidGroup.findIndex((emp) => emp.eid === employeeRow.eid);
    if (idx === -1) return false;
  }
  return true;
};

export const buildPayloadForAllotmentCalculation = (bidData, year, month, standardEmpCount, reliefEmpCount) => {
  const payload = {
    workgroup: bidData.workgroup,
    station: bidData.station,
    month: month,
    year: year,
    standardEmpCount: standardEmpCount,
    reliefEmpCount: reliefEmpCount,
  };
  return payload;
};

export const calcStandardAllotmentInitialState = {
  amCount: 0,
  pmCount: 0,
  eitherCount: 0,
  amOrPmCount: 0,
};

export const calcReliefAllotmentInitialState = {
  amCount: 0,
  pmCount: 0,
  amOrPmCount: 0,
};

export const getBidGroupColumns = (
  {
    bidReferenceNumber,
    bidGroup,
    contract,
    seniorityListType,
    workgroup,
    bidStatus,
    rankStyle,
    freedayBidMenuStyle,
    initialBidGroup,
    userSessionEid,
    bidStation,
  },
  handleIneligibleClick,
  handleRemoveEmployee,
  handleAddRemovedEmployee,
  handleEmployeeChoicesAsAdmin,
  handleChangeStandardReliefField,
  setSelectedEmployeeForAudit
) => {
  let columns = [];
  let baseColumns = [
    {
      field: "ineligible",
      headerName: "Ineligible",
      width: 80,
      type: "boolean",
      sortable: false,
      renderCell: (params) => {
        return (
          <Checkbox
            checked={!params.row.isEligible}
            onChange={() => {
              handleIneligibleClick(params.row.eid);
            }}
            disabled={
              !!params.row.isRemoved || params.row.rank === "-" || isDeletedBid(bidStatus) || isAwardedBid(bidStatus)
            }
          />
        );
      },
    },
    {
      field: "rank",
      headerName: "Rank",
      minWidth: 70,
      type: "string",
      sortingOrder: ["asc", "desc"],
      renderCell: (params) => {
        return (
          <TextField
            variant="standard"
            InputProps={{
              className: rankStyle,
            }}
            size="small"
            value={params.row.rank ? params.row.rank : ""}
            required
            error={params.row.rank === ""}
            disabled={!!params.row.isRemoved || isAwardedBid(bidStatus)}
          />
        );
      },
      flex: 0.12,
      sortComparator: sortSeniorityList,
      editable: isAwardedBid(bidStatus) || isDeletedBid(bidStatus) ? false : true,
    },
    {
      field: "eid",
      headerName: "EID",
      type: "string",
      sortable: false,
      minWidth: 100,
      flex: 0.15,
    },
    {
      field: "name",
      headerName: "Name",
      type: "string",
      minWidth: 220,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            {isEmployeeEligibleForFreedayBidChoices(params.row, initialBidGroup, bidStatus, userSessionEid) ? (
              <Typography
                style={{ cursor: "pointer" }}
                fontSize="14px"
                onClick={() => {
                  handleEmployeeChoicesAsAdmin(params.row);
                }}
              >
                <u>
                  {params.row.lastName}, {params.row.shortName}
                </u>
              </Typography>
            ) : (
              <>
                {params.row.lastName}, {params.row.shortName}
              </>
            )}
            {params.row.isTemp && params.row.homeStation !== bidStation && (
              <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />
            )}
          </>
        );
      },
      flex: 0.3,
    },
    {
      field: "phoneNumber",
      headerName: "Phone #",
      type: "string",
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (!params.row.phoneNumber) return "-";
        else return params.row.phoneNumber;
      },
      flex: 0.15,
    },
    {
      field: "standardRelief",
      headerName: "Standard/Relief",
      type: "string",
      minWidth: 130,
      sortable: false,
      renderCell: (params) => {
        return (
          <Select
            variant="standard"
            required
            className={freedayBidMenuStyle}
            onChange={(event) => handleChangeStandardReliefField(event, params.id)}
            value={params.row.standardRelief}
            error={params.row.standardRelief === ""}
            disabled={!!params.row.isRemoved || isAwardedBid(bidStatus)}
          >
            <MenuItem value={""} disabled={true}></MenuItem>
            {SELECT_FREEDAY_BID_TYPE_LIST.map((option) => {
              return (
                <MenuItem key={option} value={option}>
                  {option}
                </MenuItem>
              );
            })}
          </Select>
        );
      },
      editable: isAwardedBid(bidStatus) || isDeletedBid(bidStatus) ? false : true,
    },
  ];

  // TWU
  let contractBasedColumns = [];
  contractBasedColumns.push(
    ...[
      {
        field: "wnSliSeniorityDate",
        headerName: "WN SLI Seniority Date",
        type: "dateTime",
        minWidth: 170,
        sortable: false,
        renderCell: (params) => {
          if (params.row.wnSliSeniorityDate === "" || params.row.wnSliSeniorityDate === SENIORITY_MISSING_DATA)
            return "-";
          return params.row.wnSliSeniorityDate;
        },
        flex: 0.25,
      },
      {
        field: "flAtlSeniorityDate",
        headerName: "FL ATL Seniority Date",
        type: "dateTime",
        sortable: false,
        minWidth: 170,
        renderCell: (params) => {
          if (params.row.flAtlSeniorityDate === "" || params.row.flAtlSeniorityDate === SENIORITY_MISSING_DATA)
            return "-";
          return params.row.flAtlSeniorityDate;
        },
        flex: 0.25,
      },
      {
        field: "dualSeniorityDate",
        headerName: "Dual Seniority Date",
        type: "dateTime",
        minWidth: 170,
        sortable: false,
        renderCell: (params) => {
          if (params.row.dualSeniorityDate === "" || params.row.dualSeniorityDate === SENIORITY_MISSING_DATA)
            return "-";
          return params.row.dualSeniorityDate;
        },
        flex: 0.25,
      },
      {
        field: "hireDate",
        headerName: "Hire Date",
        type: "dateTime",
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          if (params.row.hireDate === "" || params.row.hireDate === SENIORITY_MISSING_DATA) return "-";
          return params.row.hireDate;
        },
        flex: 0.15,
      },
      {
        field: "birthDate",
        headerName: "Birth Date",
        type: "dateTime",
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          if (params.row.birthDate === "" || params.row.birthDate === SENIORITY_MISSING_DATA) return "-";
          return params.row.birthDate;
        },
        flex: 0.15,
      },
      {
        field: "baseSchedule",
        headerName: "Base Schedule",
        type: "string",
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          if (isEmployeeEligibleForBaseSchedule(params.row, initialBidGroup, bidStatus, bidStation)) {
            return (
              <Link
                to={{
                  pathname: `/${FEATURES.SCHEDULE_MANAGEMENT}/${bidReferenceNumber}/${params.row.eid}`,
                }}
                target="_blank"
              >
                <IconButton>
                  <CalendarMonthIcon />
                </IconButton>
              </Link>
            );
          }
        },
        flex: 0.15,
      },
    ]
  );

  const staticRightColumns = [
    {
      headerName: "",
      field: "comments",
      type: "string",
      width: 25,
      sortable: false,
      renderCell: (params) => {
        if (params?.row?.comments) {
          return <CommentIcon sx={{ cursor: "pointer" }} onClick={() => setSelectedEmployeeForAudit(params?.row)} />;
        }
      },
    },
    {
      field: "removeOrAddEmployee",
      headerName: "",
      width: 20,
      renderCell: (params) => {
        if (!params?.row?.isRemoved) {
          return (
            <IconButton
              disabled={isDeletedBid(bidStatus) || isAwardedBid(bidStatus)}
              sx={{ cursor: "pointer" }}
              onClick={() => handleRemoveEmployee(params.row)}
            >
              <RemoveCircleOutline fontSize="small" />
            </IconButton>
          );
        }

        return (
          <Tooltip
            title={
              getEmployeeUnrankedCount(getEligibleEmployees(bidGroup)) > 0 && BID_GROUP_ADD_EMPLOYEE_WARNING_MESSAGE
            }
            placement="bottom"
            arrow
          >
            <div>
              <IconButton
                disabled={
                  isDeletedBid(bidStatus) ||
                  getEmployeeUnrankedCount(getEligibleEmployees(bidGroup)) > 0 ||
                  isAwardedBid(bidStatus)
                }
                sx={{ color: "secondary.swaGray", cursor: "pointer" }}
                onClick={() => handleAddRemovedEmployee(params.row)}
              >
                <AddCircle fontSize="small" />
              </IconButton>
            </div>
          </Tooltip>
        );
      },
    },
  ];

  columns.push(...baseColumns);
  columns.push(...contractBasedColumns);
  columns.push(...staticRightColumns);

  return columns;
};

export const getDefaultCalendarMonth = (schedMonth, schedYear) => {
  const formattedDate = DateTime.fromFormat(`${schedMonth} ${schedYear}`, "MMMM yyyy");
  return formattedDate.toJSDate();
};

export const getDayOfTheWeek = (dateString) => {
  return DateTime.fromFormat(dateString, "yyyy-MM-dd").toFormat("ccc").toUpperCase();
};

export const getFormattedMonthAndDate = (dateString) => {
  return DateTime.fromFormat(dateString, "yyyy-MM-dd").toFormat("MM/dd");
};

export const getUnAwardedTableColumns = ({ station, bidReferenceNumber, bidStatus }) => {
  let columns = [
    {
      field: "awardType",
      headerName: "Bid",
      sortable: false,
      width: 55,
    },
    {
      field: "rank",
      headerName: "Rank",
      sortable: false,
      width: 60,
    },
    {
      field: "eid",
      headerName: "EID",
      sortable: false,
      width: 80,
    },
    {
      field: "name",
      headerName: "Name",
      sortable: false,
      width: 170,
      renderCell: (params) => {
        return (
          <>
            {params.row.lastName && params.row.shortName ? params.row.lastName + ", " + params.row.shortName : ""}
            {params.row.isTemp && params.row.homeStation !== station && (
              <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />
            )}
          </>
        );
      },
    },
    {
      field: "awardBsAmPm",
      headerName: "Shift",
      sortable: false,
      width: 60,
    },
    {
      field: "standardRelief",
      headerName: "Standard/ Relief",
      sortable: false,
      width: 82,
    },
    {
      field: "baseSchedule",
      headerName: "Base Schedule",
      type: "string",
      minWidth: 120,
      sortable: false,
      renderCell: (params) => {
        if (
          !isDeletedBid(bidStatus) &&
          !isAwardedBid(bidStatus) &&
          params.row.awardType === "NS" &&
          !(params.row.isTemp && params.row.homeStation !== station)
        ) {
          return (
            <Link
              to={{
                pathname: `/${FEATURES.SCHEDULE_MANAGEMENT}/${bidReferenceNumber}/${params.row.eid}`,
              }}
              target="_blank"
            >
              <IconButton>
                <CalendarMonthIcon />
              </IconButton>
            </Link>
          );
        }
      },
      flex: 0.15,
    },
  ];

  return columns;
};

export const getFreedayColumns = ({ station, handleAwardedNameClick }) => {
  let columns = [
    {
      field: "date",
      headerName: "Day",
      sortable: false,
      width: 45,
      renderCell: (params) => {
        return params.row.date ? DateTime.fromFormat(params.row.date, "yyyy-MM-dd").toFormat("MM/dd") : params.row.id;
      },
    },
    {
      field: "awardType",
      headerName: "Award",
      sortable: false,
      width: 60,
      renderCell: (params) => {
        return params.row.awardType;
      },
    },
    {
      field: "awardedRank",
      headerName: "Rank",
      sortable: false,
      width: 60,
      renderCell: (params) => {
        return params.row.awardedRank;
      },
    },
    {
      field: "awardedEid",
      headerName: "EID",
      sortable: false,
      width: 90,
      renderCell: (params) => {
        return params.row.eid;
      },
    },
    {
      field: "awardedName",
      headerName: "Name",
      sortable: false,
      width: 170,
      renderCell: (params) => {
        return (
          <>
            <Typography
              style={{ cursor: "pointer" }}
              fontSize="14px"
              onClick={() => {
                handleAwardedNameClick(params.row);
              }}
            >
              <u>
                {params.row.lastName && params.row.firstName ? params.row.lastName + ", " + params.row.firstName : ""}
              </u>
            </Typography>
            {params.row.isTemp && params.row.homeStation !== station && (
              <Chip sx={{ marginLeft: "5px" }} label={EMPLOYEE_TEMP_LABEL} />
            )}
          </>
        );
      },
    },
    {
      field: "awardedAllotment",
      headerName: "Allotment",
      sortable: false,
      width: 110,
      renderCell: (params) => {
        return params.row.awardedAllotment;
      },
    },
    {
      field: "standardRelief",
      headerName: "Standard/ Relief",
      sortable: false,
      width: 120,
      renderCell: (params) => {
        return params.row.standardRelief;
      },
    },
  ];
  return columns;
};

// Below function create records for each day with different allotment types(AM/PM/EITHER)
export const processDayStateForFreedayAward = (dayState, standardRelief, bidGroup) => {
  return dayState.flatMap((obj) => {
    const result = [];
    if (obj.awardedEmps?.length > 0 && Array.isArray(obj.awardedEmps)) {
      let amPmEmps,
        amEmps,
        pmEmps,
        eitherEmps = 0;
      if (obj.amOrPmCount > 0) {
        amPmEmps = obj.awardedEmps.filter(
          (awardedEmp) =>
            (awardedEmp.awardedAllotment === "AM" || awardedEmp.awardedAllotment === "PM") &&
            bidGroup.some(
              (bidGroupEmp) => bidGroupEmp["eid"] === awardedEmp.eid && bidGroupEmp["standardRelief"] === standardRelief
            )
        );
      } else {
        amEmps = obj.awardedEmps.filter(
          (awardedEmp) =>
            awardedEmp.awardedAllotment === "AM" &&
            bidGroup.some(
              (bidGroupEmp) => bidGroupEmp["eid"] === awardedEmp.eid && bidGroupEmp["standardRelief"] === standardRelief
            )
        );

        pmEmps = obj.awardedEmps.filter(
          (awardedEmp) =>
            awardedEmp.awardedAllotment === "PM" &&
            bidGroup.some(
              (bidGroupEmp) => bidGroupEmp["eid"] === awardedEmp.eid && bidGroupEmp["standardRelief"] === standardRelief
            )
        );
        eitherEmps = obj.awardedEmps.filter(
          (awardedEmp) =>
            (awardedEmp.awardedAllotment === ALLOTMENT_TYPES.AM_EITHER ||
              awardedEmp.awardedAllotment === ALLOTMENT_TYPES.PM_EITHER) &&
            bidGroup.some(
              (bidGroupEmp) => bidGroupEmp["eid"] === awardedEmp.eid && bidGroupEmp["standardRelief"] === standardRelief
            )
        );
      }

      for (let i = 0; i < obj.amOrPmCount; i++) {
        const ampmEmp = amPmEmps[i];
        if (ampmEmp) {
          const bidGroupEmp = bidGroup.find((emp) => emp.eid === ampmEmp.eid);
          result.push({
            ...ampmEmp,
            date: obj.date,
            standardRelief: standardRelief,
            isManuallyAddedToBidGroup: bidGroupEmp["isManuallyAddedToBidGroup"],
          });
        } else {
          result.push({ date: obj.date, awardedAllotment: "EITHER", standardRelief: standardRelief });
        }
      }

      for (let i = 0; i < obj.amCount; i++) {
        const amEmp = amEmps[i];
        if (amEmp) {
          const bidGroupEmp = bidGroup.find((emp) => emp.eid === amEmp.eid);
          result.push({
            ...amEmp,
            date: obj.date,
            awardedAllotment: "AM",
            standardRelief: standardRelief,
            isManuallyAddedToBidGroup: bidGroupEmp["isManuallyAddedToBidGroup"],
          });
        } else {
          result.push({ date: obj.date, awardedAllotment: "AM", standardRelief: standardRelief });
        }
      }

      for (let i = 0; i < obj.eitherCount; i++) {
        const eitherEmp = eitherEmps[i];
        if (eitherEmp) {
          const bidGroupEmp = bidGroup.find((emp) => emp.eid === eitherEmp.eid);
          result.push({
            ...eitherEmp,
            date: obj.date,
            standardRelief: standardRelief,
            isManuallyAddedToBidGroup: bidGroupEmp["isManuallyAddedToBidGroup"],
          });
        } else {
          result.push({ date: obj.date, awardedAllotment: "EITHER", standardRelief: standardRelief });
        }
      }

      for (let i = 0; i < obj.pmCount; i++) {
        const pmEmp = pmEmps[i];
        if (pmEmp) {
          const bidGroupEmp = bidGroup.find((emp) => emp.eid === pmEmp.eid);
          result.push({
            ...pmEmp,
            date: obj.date,
            awardedAllotment: "PM",
            standardRelief: standardRelief,
            isManuallyAddedToBidGroup: bidGroupEmp["isManuallyAddedToBidGroup"],
          });
        } else {
          result.push({ date: obj.date, awardedAllotment: "PM", standardRelief: standardRelief });
        }
      }
    } else {
      //Remaining unawarded AM,PM etc. records in each day
      for (let i = 0; i < obj.amCount; i++) {
        result.push({ date: obj.date, awardedAllotment: "AM", standardRelief: standardRelief });
      }
      for (let i = 0; i < obj.pmCount; i++) {
        result.push({ date: obj.date, awardedAllotment: "PM", standardRelief: standardRelief });
      }
      for (let i = 0; i < obj.eitherCount; i++) {
        result.push({ date: obj.date, awardedAllotment: "EITHER", standardRelief: standardRelief });
      }
      for (let i = 0; i < obj.amOrPmCount; i++) {
        //Displaying as EITHER for AMORPM also
        result.push({ date: obj.date, awardedAllotment: "EITHER", standardRelief: standardRelief });
      }
    }

    return result;
  });
};

export const generateDayStateRows = (standardDayState, reliefDayState, bidGroup) => {
  const standardResults = processDayStateForFreedayAward(standardDayState, "Standard", bidGroup);
  const reliefResults = processDayStateForFreedayAward(reliefDayState, "Relief", bidGroup);
  const allResults = [...standardResults, ...reliefResults];
  allResults.sort((a, b) => {
    if (a.date !== b.date) {
      return a.date.localeCompare(b.date);
    }
    if (a.standardRelief !== b.standardRelief) {
      return a.standardRelief === "standard" ? -1 : 1;
    }
    const awardTypeOrder = { AM: 0, "AM (Either)": 1, EITHER: 2, "PM (Either)": 3, PM: 4 };
    return awardTypeOrder[a.awardedAllotment] - awardTypeOrder[b.awardedAllotment];
  });

  // Add unique IDs to the final sorted array
  const finalResults = allResults.map((obj, index) => ({
    id: index + 1,
    ...obj,
  }));
  return finalResults;
};

export const hasUnawardedFreedayEmployees = (employees) => {
  return (
    employees.filter((emp) => !emp.isRemoved && emp.isEligible && (!emp.awardedDays || emp.awardedDays.length === 0))
      .length > 0
  );
};
